// Import-free contract shared by the portal, Edge Functions and tests.
export const WAREHOUSE_FIELD_ID = 'VEND.X200';
export const WAREHOUSE_LINES = ['Primis Bank', 'First Funding'] as const;
export const WAREHOUSE_STALE_SEND_MS = 10 * 60 * 1000;
export type WarehouseLine = typeof WAREHOUSE_LINES[number];
export type WarehouseValue = WarehouseLine | '';

export interface WarehouseWrite {
  requestId: string;
  auditId: string;
  value: string;
  status: 'pending' | 'syncing' | 'synced' | 'failed' | 'cancelled';
  error: string | null;
  outcomeUnknown: boolean;
  lastAttemptAt: string | null;
}

export interface WarehouseReceipt {
  requestId: string;
  auditId: string;
  value: string;
  status: 'queued' | 'synced' | 'failed' | 'superseded' | 'cancelled';
  error: string | null;
}

export interface WarehouseSnapshot {
  loanId: string;
  encompassLoanId: string;
  value: string | null;
  revision: string;
  write: WarehouseWrite | null;
  requests: WarehouseReceipt[];
}

export function isWarehouseValue(value: unknown): value is WarehouseValue {
  return value === '' || WAREHOUSE_LINES.some(line => line === value);
}

export function matchWarehouseLine(value: string | null | undefined): WarehouseLine | null {
  const normalized = value?.trim().toLowerCase();
  return WAREHOUSE_LINES.find(line => line.toLowerCase() === normalized) ?? null;
}

export function warehouseFromEntity(entity: unknown): string | null {
  if (!entity || typeof entity !== 'object' || Array.isArray(entity)) {
    throw new Error('Warehouse entity is unavailable');
  }
  const contacts = (entity as { contacts?: unknown }).contacts;
  if (contacts !== undefined && !Array.isArray(contacts)) {
    throw new Error('Warehouse contacts are malformed');
  }
  const contact = (contacts ?? []).find((item: unknown) =>
    item && typeof item === 'object' && (item as { contactType?: unknown }).contactType === 'WAREHOUSE'
  ) as { name?: unknown } | undefined;
  if (contact?.name !== undefined && contact.name !== null && typeof contact.name !== 'string') {
    throw new Error('Warehouse name is malformed');
  }
  return typeof contact?.name === 'string' ? contact.name.trim() || null : null;
}

export function warehouseUnresolved(snapshot: WarehouseSnapshot | null): boolean {
  const write = snapshot?.write;
  return !!write && (write.outcomeUnknown || ['pending', 'syncing', 'failed'].includes(write.status));
}

export function warehouseSendActive(write: WarehouseWrite | null, now = Date.now()): boolean {
  if (write?.status !== 'syncing') {
    return false;
  }
  const attempted = Date.parse(write.lastAttemptAt ?? '');
  return Number.isFinite(attempted) && now - attempted < WAREHOUSE_STALE_SEND_MS;
}

export function newestWarehouseSnapshot(
  previous: WarehouseSnapshot | null,
  incoming: WarehouseSnapshot
): WarehouseSnapshot {
  if (!previous || previous.loanId !== incoming.loanId || BigInt(incoming.revision) > BigInt(previous.revision)) {
    return incoming;
  }
  // Receipt lookups can add history without changing canonical state. A receipt
  // from an older snapshot must never replace an already newer receipt.
  if (BigInt(incoming.revision) === BigInt(previous.revision)) {
    const receipts = new Map(previous.requests.map(receipt => [receipt.requestId, receipt]));
    incoming.requests.forEach(receipt => receipts.set(receipt.requestId, receipt));
    return { ...previous, requests: [...receipts.values()] };
  }
  return previous;
}
